import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  adminApiClientCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { adminEndpoints } from "@/features/admin/api/endpoints";
import type {
  ApiClientDto,
  UpdateApiClientScopesDto,
} from "@/features/admin/api/dto";

/**
 * Remplace les scopes d'un client API (`PUT /ApiClients/{id}/scopes` —
 * endpoint gateway, §6.1). Rôles : flashpay-superadmin, flashpay-admin.
 */
export const useUpdateApiClientScopes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["admin", "api-clients", "scopes"],
    mutationFn: ({
      apiClientId,
      scopes,
    }: {
      apiClientId: string;
      scopes: string[];
    }) =>
      mutateData({
        url: adminEndpoints.apiClients.scopes(apiClientId),
        reqBody: { scopes } satisfies UpdateApiClientScopesDto,
        method: "PUT",
      }) as Promise<ApiClientDto>,
    onSuccess: (_data, { apiClientId }) => {
      return invalidateAfterMutation(
        queryClient,
        adminApiClientCacheKeys(apiClientId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUpdateApiClientScopes failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "admin" }
      );
    },
  });
};
